import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentGroupReassignmentReason,
  FulfillmentGroupReassignmentReasonLabels,
} from "@redotech/redo-fulfillment-group-common/reassignment-reason";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useState } from "react";

const REASON_OPTIONS = Object.values(FulfillmentGroupReassignmentReason);

export const AssignToMerchantModal = memo(function AssignToMerchantModal({
  isOpen,
  fulfillmentGroup,
  warehouseName,
  pending,
  rerouteFailed,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  fulfillmentGroup: FulfillmentGroupData | null;
  warehouseName: string;
  pending: boolean;
  rerouteFailed: boolean;
  onClose: () => void;
  onConfirm: (input: {
    reason: FulfillmentGroupReassignmentReason;
    note: string;
  }) => void;
}) {
  const [reason, setReason] =
    useState<FulfillmentGroupReassignmentReason | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason(null);
      setNote("");
    }
  }, [isOpen]);

  const destinationLabel = fulfillmentGroup?.teamInfo?.name ?? "Merchant";
  const orderLabel = fulfillmentGroup?.orders?.[0]?.name ?? "Order";

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: `Assign to ${destinationLabel}`,
        disabled: pending || !fulfillmentGroup || !reason,
        pending,
        onClick: () => {
          if (reason) {
            onConfirm({ reason, note: note.trim() });
          }
        },
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={orderLabel}
      title={`Assign to ${destinationLabel}`}
    >
      <Flex dir="column" gap="lg">
        <RedoSingleSelectDropdownInput
          label="Reason"
          options={REASON_OPTIONS.map((value) => ({
            type: "text" as const,
            id: value,
            text: FulfillmentGroupReassignmentReasonLabels[value],
            value,
          }))}
          optionSelected={(args) => {
            setReason(args.item.value);
          }}
          placeholder="Select reason"
          selectedItem={
            reason
              ? {
                  type: "text",
                  id: reason,
                  text: FulfillmentGroupReassignmentReasonLabels[reason],
                  value: reason,
                }
              : undefined
          }
        />
        <RedoTextareaInputField
          autoExpand
          label="Note (optional)"
          placeholder="Add note for the merchant ..."
          setValue={setNote}
          value={note}
        />
        {rerouteFailed ? (
          <RedoAlert
            color="error"
            description={`Shopify couldn't find a ${destinationLabel} location that can ship this order, so it was not assigned. Please contact ${destinationLabel} to reassign the order.`}
            fullWidth
            size="sm"
            title="Shopify couldn't reroute this order"
          />
        ) : (
          <RedoAlert
            color="gray"
            description={`After ${warehouseName} releases this order, Shopify routes it to a ${destinationLabel} location.`}
            fullWidth
            size="sm"
            title="Shopify chooses the location"
          />
        )}
      </Flex>
    </RedoModal>
  );
});
